import { create } from 'zustand';
import type { PlanTier, TenantRole } from '@/types';
import { API_URL } from '@/lib/apiUrl';
import { getCsrfToken } from '@/lib/csrf';
import { useBuilderStore } from './builderStore';
// Lazy import avoids circular deps — resolved at call time
import { useIntakeStore } from '../hooks/useIntakeStore';

// Rotate the session cookie via the httpOnly refresh cookie (7-day). Raw fetch (not apiClient) to
// avoid a store↔client import cycle; mirrors client.ts refreshSession. Returns true on success.
async function refreshSessionCookie(): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}/auth/refresh/cookie`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken(false) ?? '' },
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Cookie-auth store (Wave 2.4 cutover).
 *
 * There is no JS-accessible access token any more — the session lives in httpOnly
 * cookies the browser sends automatically. Identity is hydrated from GET /auth/me,
 * NOT decoded from a token in JS. `status` is a three-state machine so the Router
 * can show a loading shell until hydration resolves (avoids a login-flash on reload).
 */

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface MeResponse {
  user: { id: string; email: string; role: string } | null;
  tenant: { id: string; slug: string; plan: string; status: string } | null;
  role: string | null;
}

interface AuthState {
  status: AuthStatus;
  /** Derived convenience flag (status === 'authenticated'). */
  isAuthenticated: boolean;
  /** True while an admin is impersonating a tenant user (selects the CSRF cookie). */
  impersonating: boolean;

  userId: string | null;
  tenantId: string | null;
  tenantSlug: string | null;
  role: TenantRole | null;
  /** System-level role: 'user' | 'superadmin' (from /auth/me user.role). */
  userRole: string | null;
  plan: PlanTier | null;
  email: string | null;
  /**
   * Multi-site: the site currently being edited (the `/studio/:siteId` URL drives it). Sent as
   * the `X-Site-Id` header on every API call so the gateway scopes to this site. null = the
   * tenant's primary site (the gateway falls back to it when the header is absent).
   */
  activeSiteId: string | null;

  /** Fetch /auth/me and populate identity. Resolves to true when authenticated. */
  hydrate: () => Promise<boolean>;
  setImpersonating: (value: boolean) => void;
  /** Set the active site being edited (null → the tenant's primary). */
  setActiveSiteId: (id: string | null) => void;
  /** Local-only state reset (also broadcast to other tabs). Does NOT call the API. */
  logout: () => void;
  updatePlan: (plan: PlanTier) => void;
}

const UNAUTH: Omit<
  AuthState,
  'hydrate' | 'setImpersonating' | 'setActiveSiteId' | 'logout' | 'updatePlan' | 'status' | 'isAuthenticated' | 'impersonating'
> = {
  userId: null,
  tenantId: null,
  tenantSlug: null,
  role: null,
  userRole: null,
  plan: null,
  email: null,
  activeSiteId: null,
};

// ── Cross-tab logout via BroadcastChannel ────────────────────────────────────
// One tab logging out (or a 401 that can't refresh) tells the others to drop auth.
const authChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('rs_auth') : null;

export const useAuthStore = create<AuthState>()((set) => ({
  status: 'loading',
  isAuthenticated: false,
  impersonating: false,
  ...UNAUTH,

  hydrate: async () => {
    const fetchMe = () =>
      fetch(`${API_URL}/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    try {
      let res = await fetchMe();
      // A cold load (reload / new deploy) runs BEFORE the keep-alive interval fires, so an expired
      // 15-min access token 401s here. This raw fetch bypassed the apiClient interceptor's
      // refresh-on-401, so a still-valid 7-day session was dropped straight to /login. Rotate the
      // session via the refresh cookie ONCE and retry — the fix for "reload always logs me out".
      if (res.status === 401) {
        const refreshed = await refreshSessionCookie();
        if (refreshed) res = await fetchMe();
      }
      if (!res.ok) {
        set({ status: 'unauthenticated', isAuthenticated: false, ...UNAUTH });
        return false;
      }
      const body = (await res.json()) as { data: MeResponse };
      const { user, tenant, role } = body.data;
      if (!user) {
        set({ status: 'unauthenticated', isAuthenticated: false, ...UNAUTH });
        return false;
      }
      set({
        status: 'authenticated',
        isAuthenticated: true,
        userId: user.id,
        email: user.email,
        userRole: user.role ?? null,
        tenantId: tenant?.id ?? null,
        tenantSlug: tenant?.slug ?? null,
        plan: (tenant?.plan as PlanTier) ?? null,
        role: (role as TenantRole) ?? null,
      });
      return true;
    } catch {
      set({ status: 'unauthenticated', isAuthenticated: false, ...UNAUTH });
      return false;
    }
  },

  setImpersonating: (value: boolean) => set({ impersonating: value }),

  setActiveSiteId: (id: string | null) => set({ activeSiteId: id }),

  logout: () => {
    // Reset site-specific stores so no data bleeds into the next session.
    useBuilderStore.getState().reset();
    useIntakeStore.getState().reset();
    // Drop persisted intake draft (was the only auth-adjacent sessionStorage key).
    try {
      sessionStorage.removeItem('bls-intake-store');
    } catch {
      // ignore (private mode / SSR)
    }
    set({ status: 'unauthenticated', isAuthenticated: false, impersonating: false, ...UNAUTH });
    // Tell other tabs to drop auth too (guard against echo loops with a type tag).
    authChannel?.postMessage({ type: 'logout' });
  },

  updatePlan: (plan: PlanTier) => set({ plan }),
}));

// Other-tab logout: clear local auth WITHOUT re-broadcasting (avoid an echo loop).
if (authChannel) {
  authChannel.onmessage = (e: MessageEvent) => {
    if (e.data?.type === 'logout' && useAuthStore.getState().status !== 'unauthenticated') {
      useBuilderStore.getState().reset();
      useIntakeStore.getState().reset();
      useAuthStore.setState({
        status: 'unauthenticated',
        isAuthenticated: false,
        impersonating: false,
        ...UNAUTH,
      });
    }
  };
}
